import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { apiSilentRequestOptions } from 'app/shared/interceptors/api-message-context';

export type IptvClientFlags = {
    hide_vpn: boolean;
    hide_login: boolean;
    hide_tnc: boolean;
};

const STORAGE_KEY = 'ashcorp_iptv_hide_login';

@Injectable({ providedIn: 'root' })
export class IptvClientFlagsService {
    private _flags: IptvClientFlags | null = null;
    private _inFlight$: Observable<IptvClientFlags> | null = null;

    constructor(private _http: HttpClient) {
        if (this._readStoredHideLogin()) {
            this._flags = { hide_vpn: false, hide_login: true, hide_tnc: false };
        }
    }

    get hideLogin(): boolean {
        return this._flags?.hide_login === true || this._readStoredHideLogin();
    }

    load(): Observable<IptvClientFlags> {
        if (this._inFlight$) {
            return this._inFlight$;
        }

        this._inFlight$ = this._http
            .get<{ iptv?: Partial<IptvClientFlags> }>(
                '/api/authentication/terms/version',
                apiSilentRequestOptions()
            )
            .pipe(
                timeout(4000),
                map((payload) => this._normalize(payload?.iptv)),
                tap((flags) => this._store(flags)),
                catchError(() => of(this._flags ?? this._normalize(null))),
                finalize(() => {
                    this._inFlight$ = null;
                }),
                shareReplay(1)
            );

        return this._inFlight$;
    }

    private _store(flags: IptvClientFlags): void {
        this._flags = flags;
        try {
            sessionStorage.setItem(STORAGE_KEY, flags.hide_login ? '1' : '0');
        } catch {
            /* ignore */
        }
    }

    private _normalize(raw: Partial<IptvClientFlags> | null | undefined): IptvClientFlags {
        return {
            hide_vpn: raw?.hide_vpn === true,
            hide_login: raw?.hide_login === true,
            hide_tnc: raw?.hide_tnc === true,
        };
    }

    private _readStoredHideLogin(): boolean {
        try {
            return sessionStorage.getItem(STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    }
}
